// Must run before frappe-ui network code
import './frappeFetchPatch'
import './frappeXhrPatch'
import './style.css'

import { createApp } from 'vue'
import { FrappeUI } from 'frappe-ui'
import { io } from 'socket.io-client'
import { initThemeFromStorage } from './composables/useTheme'
import router from './router'
import App from './App.vue'
import { resolveSiteName, socketPath } from './config'
import { installQuery } from './composables/useQueryClient'
import { attachRealtime } from './composables/realtime'
import { watch } from 'vue'
import { isLoggedIn, tokenStorage } from './session'

initThemeFromStorage()

const app = createApp(App)
app.use(router)
installQuery(app)

// Default frappe-ui socket hardcodes :9000 — we use same-origin + env path instead.
app.use(FrappeUI, { socketio: false })

// Site name from env (dev) or hostname (prod). Boot may refine window.site_name later.
window.site_name = resolveSiteName()

try {
  const site = resolveSiteName()
  // Realtime auth: the SPA authenticates with a bearer JWT and has no Frappe `sid`
  // cookie, so the socket must carry `Authorization` itself. Browsers cannot set
  // headers on a WebSocket, so the handshake (and the namespace connect, where
  // Frappe's authenticate middleware runs) goes over polling with extraHeaders,
  // then upgrades. Connect only once logged in; disconnect on logout. An
  // unauthenticated socket used to reach Frappe's subscribe handlers and crash
  // the socketio process (see artifacts/ui-audit REPORT, D-RT-1).
  const socket = io(`/${site}`, {
    path: socketPath,
    withCredentials: true,
    transports: ['polling', 'websocket'],
    reconnectionAttempts: 8,
    autoConnect: false,
  })
  const authHeaders = (): Record<string, string> | null => {
    const token = tokenStorage.accessToken
    return token ? { Authorization: `Bearer ${token}` } : null
  }
  const applyAuth = (): boolean => {
    const headers = authHeaders()
    socket.io.opts.extraHeaders = headers || {}
    return !!headers
  }
  // Tokens rotate on refresh; pick up the current one before every reconnect.
  socket.io.on('reconnect_attempt', () => {
    if (!applyAuth()) socket.disconnect()
  })
  watch(
    isLoggedIn,
    (loggedIn) => {
      if (loggedIn && applyAuth()) {
        if (!socket.connected) socket.connect()
      } else if (socket.connected || socket.active) {
        socket.disconnect()
      }
    },
    { immediate: true },
  )
  socket.on('connect_error', (err: Error) => {
    if (import.meta.env.DEV) {
      console.warn('[socket]', err?.message || err)
    }
  })
  // list_update / doc_update are handled in `composables/realtime.ts`, against
  // the real vue-query keys. The old `['list', doctype]` / `['doc', doctype,
  // name]` literals here were frappe-ui store keys that nothing registered, so
  // the handlers could never do anything (`CACHE_AUDIT.md` C-03, C1.4d/C1.4e).
  window.__glamora_socket = socket
  app.config.globalProperties.$socket = socket
  attachRealtime()
} catch (e) {
  console.warn('[socket] init failed', e)
  app.config.globalProperties.$socket = null
}

app.mount('#app')
